import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, CheckCircle, Navigation, MapPin, Car, Clock, Phone, User, Star, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'confirmed', label: 'Confirmed', desc: 'Booking confirmed', icon: CheckCircle, color: 'blue' },
  { id: 'driver_assigned', label: 'Driver Assigned', desc: 'Your driver is ready', icon: User, color: 'blue' },
  { id: 'en_route', label: 'En Route', desc: 'Driver heading to pickup', icon: Navigation, color: 'yellow' },
  { id: 'arriving', label: 'Arriving', desc: 'Driver is almost there', icon: MapPin, color: 'orange' },
  { id: 'in_service', label: 'In Service', desc: 'Your ride is active', icon: Car, color: 'green' },
  { id: 'complete', label: 'Complete', desc: 'Ride finished', icon: CheckCircle, color: 'green' },
];

const RatingModal = ({ bookingId, driverName, onClose, onRated }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/dispatch/rate-driver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, rating, comment })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setTimeout(() => { onRated(); }, 2000);
      }
    } catch { }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="rating-success">
        <Card className="w-full max-w-sm p-8 text-center bg-white">
          <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-black mb-2">Thank you!</h3>
          <p className="text-sm text-gray-500">Your feedback helps us maintain the ACE standard.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="rating-modal">
      <Card className="w-full max-w-sm p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-black">Rate Your Ride</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {driverName && (
          <p className="text-sm text-gray-500 mb-4">How was your experience with <strong>{driverName}</strong>?</p>
        )}

        {/* Stars */}
        <div className="flex justify-center gap-2 mb-6" data-testid="star-rating">
          {[1, 2, 3, 4, 5].map(s => (
            <button
              key={s}
              onClick={() => setRating(s)}
              onMouseEnter={() => setHoverRating(s)}
              onMouseLeave={() => setHoverRating(0)}
              className="transition-transform hover:scale-110"
              data-testid={`star-${s}`}
            >
              <Star className={`h-10 w-10 ${
                s <= (hoverRating || rating)
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300'
              }`} />
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-gray-500 mb-4">
          {rating === 1 && 'Poor'}
          {rating === 2 && 'Fair'}
          {rating === 3 && 'Good'}
          {rating === 4 && 'Great'}
          {rating === 5 && 'Exceptional'}
        </p>

        <Textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Tell us more about your experience (optional)..."
          rows={3}
          className="mb-4 border-gray-200"
          data-testid="rating-comment"
        />

        <Button
          onClick={submit}
          disabled={rating < 1 || submitting}
          className="w-full bg-red-600 hover:bg-red-700 text-white h-12"
          data-testid="submit-rating-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Rating'}
        </Button>
      </Card>
    </div>
  );
};

const ClientTracker = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRating, setShowRating] = useState(false);
  const [ratingStatus, setRatingStatus] = useState(null);

  const loadTracking = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tracking/booking/${bookingId}`);
      if (res.ok) setTracking(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [bookingId]);

  // Check if rating is needed
  const checkRating = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dispatch/rate-driver/${bookingId}`);
      if (res.ok) {
        const data = await res.json();
        setRatingStatus(data);
        // Auto-show rating modal when ride is complete and not yet rated
        if (data.is_complete && !data.has_rated) {
          setShowRating(true);
        }
      }
    } catch { }
  }, [bookingId]);

  useEffect(() => { loadTracking(); }, [loadTracking]);
  useEffect(() => { if (tracking?.status === 'complete') checkRating(); }, [tracking?.status, checkRating]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    if (tracking?.status === 'complete') return;
    const interval = setInterval(loadTracking, 15000);
    return () => clearInterval(interval);
  }, [tracking?.status, loadTracking]);

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;
  if (!tracking) return <div className="min-h-screen bg-white flex items-center justify-center"><p className="text-gray-600">Booking not found.</p></div>;

  const currentIdx = STEPS.findIndex(s => s.id === tracking.status);
  const isComplete = tracking.status === 'complete';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-tracker">
      <header className="bg-black text-white py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal/bookings')} className="text-gray-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-xs text-red-500 font-semibold tracking-widest">LIVE TRACKER</p>
            <h1 className="text-lg font-bold">Booking {bookingId?.slice(0, 12)}...</h1>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        <Card className={`p-6 border-0 ${isComplete ? 'bg-green-600' : 'bg-red-600'} text-white`} data-testid="status-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80 mb-1">Current Status</p>
              <h2 className="text-2xl sm:text-3xl font-bold" data-testid="current-status">{tracking.status_label || tracking.status?.replace('_', ' ')}</h2>
              {tracking.eta_minutes && !isComplete && (
                <div className="flex items-center gap-2 mt-2 bg-white/20 rounded-full px-3 py-1 w-fit">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-semibold" data-testid="eta-display">ETA: {tracking.eta_minutes} min</span>
                </div>
              )}
            </div>
            {!isComplete && (
              <div className="animate-pulse">
                <div className="h-4 w-4 bg-white rounded-full" />
              </div>
            )}
          </div>
        </Card>

        {/* Progress Timeline */}
        <Card className="p-6 border-gray-200" data-testid="progress-timeline">
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-6">Journey Progress</h3>
          <div className="space-y-0">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isPast = i < currentIdx;
              const isCurrent = i === currentIdx;
              const isFuture = i > currentIdx;
              return (
                <div key={step.id} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isCurrent ? 'bg-red-600 ring-4 ring-red-100' : isPast ? 'bg-green-600' : 'bg-gray-200'}`}>
                      <StepIcon className={`h-5 w-5 ${isFuture ? 'text-gray-400' : 'text-white'}`} />
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`w-0.5 h-8 ${isPast ? 'bg-green-400' : 'bg-gray-200'}`} />
                    )}
                  </div>
                  <div className={`pt-2 ${isFuture ? 'opacity-40' : ''}`}>
                    <p className={`font-semibold text-sm ${isCurrent ? 'text-red-600' : isPast ? 'text-green-600' : 'text-gray-400'}`}>{step.label}</p>
                    <p className="text-xs text-gray-500">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Trip Details */}
        <Card className="p-6 border-gray-200" data-testid="trip-details">
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Trip Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs mb-1">Service</p>
              <p className="font-semibold text-black capitalize">{tracking.service_type?.replace('-', ' ') || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Vehicle</p>
              <p className="font-semibold text-black">{tracking.vehicle_name || 'TBD'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Pickup</p>
              <p className="font-semibold text-black">{tracking.pickup_location || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Date</p>
              <p className="font-semibold text-black">{tracking.booking_date ? new Date(tracking.booking_date).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </Card>

        {/* Driver Info */}
        {tracking.driver_name && (
          <Card className="p-6 border-gray-200" data-testid="driver-info">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Your Driver</h3>
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-red-100 rounded-full flex items-center justify-center">
                <User className="h-7 w-7 text-red-600" />
              </div>
              <div>
                <p className="font-bold text-black text-lg">{tracking.driver_name}</p>
                {tracking.driver_phone && (
                  <a href={`tel:${tracking.driver_phone}`} className="flex items-center gap-1 text-sm text-red-600 hover:underline">
                    <Phone className="h-3 w-3" />{tracking.driver_phone}
                  </a>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Auto-refresh indicator */}
        {!isComplete && (
          <p className="text-center text-xs text-gray-400">
            Auto-refreshing every 15 seconds
          </p>
        )}

        {/* Completed - Show Rate Button */}
        {isComplete && ratingStatus && !ratingStatus.has_rated && (
          <Card className="p-6 border-2 border-yellow-400 bg-yellow-50 text-center" data-testid="rate-ride-card">
            <Star className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <h3 className="font-bold text-black text-lg mb-1">How was your ride?</h3>
            <p className="text-sm text-gray-600 mb-4">Your feedback helps us maintain the ACE standard{ratingStatus.driver_name ? ` and recognize ${ratingStatus.driver_name}` : ''}.</p>
            <Button onClick={() => setShowRating(true)} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold" data-testid="open-rating-btn">
              <Star className="h-4 w-4 mr-2" /> Rate Your Experience
            </Button>
          </Card>
        )}

        {isComplete && ratingStatus?.has_rated && (
          <Card className="p-4 border-green-200 bg-green-50 text-center" data-testid="already-rated">
            <p className="text-sm text-green-700 flex items-center justify-center gap-2">
              <CheckCircle className="h-4 w-4" /> You rated this ride {ratingStatus.rating}/5 stars. Thank you!
            </p>
          </Card>
        )}
      </div>

      {/* Rating Modal */}
      {showRating && (
        <RatingModal
          bookingId={bookingId}
          driverName={ratingStatus?.driver_name}
          onClose={() => setShowRating(false)}
          onRated={() => { setShowRating(false); checkRating(); }}
        />
      )}
    </div>
  );
};

export default ClientTracker;
